import { SearchOutlined } from "@ant-design/icons";
import filterIcon from "../../assets/filter.png";
import { Badge, Button, Card, Input, Popover, Select } from "antd";
import { useEffect, useRef, useState } from "react";
import { APPROVAL_USERS_ACCOUNT_API_URL } from "../../config/constants";
import { apiFetch } from "../../utils/apiClient";
import { InputAdornment, TextField } from "@mui/material";
import "../../styles/trackon.css";


const CONTRACT_CATEGORY_OPTIONS = [
  { value: "Fixed Bid/Price-Milestone", label: "Fixed Bid/Price-Milestone" },
  { value: "T & M", label: "T & M" },
  { value: "Managed Capacity", label: "Managed Capacity" },
];

const getAuthHeader = ()=> {
  const token =
    sessionStorage.getItem("token") ||
    sessionStorage.getItem("authToken") ||
    sessionStorage.getItem("accessToken");

  if (!token) return {};

  const tokenType = sessionStorage.getItem("tokenType") || "Bearer";
  const normalizedToken = token
    .replace(/^"|"$/g, "")
    .replace(/^Bearer\s+/i, "")
    .trim();

  if (!normalizedToken) return {};
  return { Authorization: `${tokenType} ${normalizedToken}` };
};

export default function ProjectReviewFilter({
  onFilterChange,
}) {
  const SEARCH_DEBOUNCE_MS = 400;
  const SEARCH_MAX_LENGTH = 100;
  const FILTER_POPUP_WIDTH = 320;

  const [filterData, setFilterData] = useState({
    search: "",
    customerName: undefined,
    contractCategory: undefined,
  });

  const [filterOpen, setFilterOpen] = useState(false);
  const [accountOptions, setAccountOptions] = useState([]);
  const [accountsLoading, setAccountsLoading] = useState(false);

  const lastAutoSearchValueRef = useRef(filterData.search);
  const filterDataRef = useRef(filterData);
  const searchTimeoutRef = useRef();
  const filterContentRef = useRef(null);

  useEffect(() => {
    filterDataRef.current = filterData;
  }, [filterData]);

  // Load customer (account) names for the filter dropdown.
  useEffect(() => {
    const controller = new AbortController();

    const fetchAccounts = async () => {
      setAccountsLoading(true);
      try {
        const response = await apiFetch(APPROVAL_USERS_ACCOUNT_API_URL, {
          method: "GET",
          headers: {
            Accept: "application/json",
            ...getAuthHeader(),
          },
          signal: controller.signal,
        }, "Failed to fetch accounts");

        if (!response.ok) {
          throw new Error(`Failed to fetch accounts: ${response.status}`);
        }

        const data = (await response.json());
        setAccountOptions(Array.isArray(data) ? data : []);
      } catch (error) {
        if ((error).name !== "AbortError") {
          console.error("Unable to load customer accounts", error);
          setAccountOptions([]);
        }
      } finally {
        setAccountsLoading(false);
      }
    };

    void fetchAccounts();

    return () => controller.abort();
  }, []);

  useEffect(() => {
    const timeoutId = globalThis.setTimeout(() => {
      const trimmedSearch = filterData.search.trim();

      // Anything less than 3 characters means "no search" is applied.
      const effectiveSearch = trimmedSearch.length < 3 ? "" : trimmedSearch;

      if (lastAutoSearchValueRef.current === effectiveSearch) {
        return;
      }

      lastAutoSearchValueRef.current = effectiveSearch;

      onFilterChange?.({
        ...filterDataRef.current,
        search: effectiveSearch,
      });
    }, SEARCH_DEBOUNCE_MS);

    searchTimeoutRef.current = timeoutId;

    return () => globalThis.clearTimeout(timeoutId);
  }, [filterData.search, onFilterChange]);

  const clearFilters = () => {
    const cleared = {
      search: "",
      customerName: undefined,
      contractCategory: undefined,
    };

    lastAutoSearchValueRef.current = "";
    globalThis.clearTimeout(searchTimeoutRef.current);
    setFilterData(cleared);
    onFilterChange?.(cleared);
    setFilterOpen(false);
  };

  const activeFilterCount = [
    filterData.customerName,
    filterData.contractCategory,
  ].filter(Boolean).length;

  const trimmedSearchLength = filterData.search.trim().length;
  const showSearchError = trimmedSearchLength > 0 && trimmedSearchLength < 3;

  const filterContent = (
    <div
    className="trackon-root"
      ref={filterContentRef}
      style={{
        width: FILTER_POPUP_WIDTH,
        maxWidth: "calc(100vw - 24px)",
        height: "auto",
        overflow: "visible",
        padding: "4px 0 0",
        boxSizing: "border-box",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 16,
        }}
      >
        <span style={{ fontWeight: 600, fontSize: 14 }}>Filters</span>

        {activeFilterCount > 0 && (
          <Button
            type="link"
            size="small"
            onClick={clearFilters}
            style={{ padding: 0, height: "auto", fontSize: 12 }}
          >
            Clear all
          </Button>
        )}
      </div>

      <div style={{ marginBottom: 14 }}>
        <div
          style={{
            fontSize: 12,
            color: "#595959",
            fontWeight: 600,
            marginBottom: 6,
          }}
        >
          Customer Name
        </div>

        <Select
          size="middle"
          placeholder="None"
          value={filterData.customerName}
          onChange={(value) =>
            setFilterData((prev) => ({
              ...prev,
              customerName: value,
            }))
          }
          allowClear
          showSearch
          loading={accountsLoading}
          optionFilterProp="label"
          style={{
            width: "100%",
            color: filterData.customerName ? undefined : "#8c8c8c",
          }}
          options={accountOptions.map((account) => ({
            value: account.accountName,
            label: account.accountName,
          }))}
        />
      </div>

      <div style={{ marginBottom: 14 }}>
        <div
          style={{
            fontSize: 12,
            color: "#595959",
            fontWeight: 600,
            marginBottom: 6,
          }}
        >
          Contract Category
        </div>

        <Select
          size="middle"
          placeholder="None"
          value={filterData.contractCategory}
          onChange={(value) =>
            setFilterData((prev) => ({
              ...prev,
              contractCategory: value,
            }))
          }
          allowClear
          style={{
            width: "100%",
            color: filterData.contractCategory ? undefined : "#8c8c8c",
          }}
          options={CONTRACT_CATEGORY_OPTIONS}
        />
      </div>

      <div
        style={{
          paddingTop: 10,
          marginTop: 4,
        }}
      >
        <Button
          type="primary"
          block
          onClick={() => {
            onFilterChange?.(filterDataRef.current);
            if (filterContentRef.current) {
              filterContentRef.current.scrollTop = 0;
            }
            setFilterOpen(false);
          }}
          style={{ borderRadius: 8 }}
        >
          Apply Filters
        </Button>
      </div>
    </div>
  );

  return (

    <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
      <div style={{ flex: 1 }}>
        <TextField
          size="small"
          fullWidth
          placeholder="Search by Customer Name or Project Name"
          value={filterData.search}
          error={showSearchError}
          inputProps={{
            maxLength: SEARCH_MAX_LENGTH,
          }}
          onChange={(e) =>
            setFilterData((prev) => ({
              ...prev,
              search: e.target.value,
            }))
          }
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchOutlined
                  style={{ color: "#8c8c8c", fontSize: 16 }}
                />
              </InputAdornment>
            ),
          }}
          sx={{
            width: "100%",
            "& .MuiOutlinedInput-root": {
              height: 40,
              borderRadius: "4px",
              fontSize: 13,
            },
          }}
        />

        <div
          style={{
            minHeight: 18,
            marginTop: 4,
            color: "#ff0000",
            fontSize: 12,
          }}
        >
          {showSearchError
            ? "Minimum 3 characters required to search."
            : ""}
        </div>
      </div>

      <Popover
      className="trackon-root"
        content={filterContent}
        trigger="click"
        open={filterOpen}
        onOpenChange={(open) => {
          setFilterOpen(open);

          if (open) {
            globalThis.setTimeout(() => {
              if (filterContentRef.current) {
                filterContentRef.current.scrollTop = 0;
              }
            }, 0);
          }
        }}
        placement="leftTop"
        autoAdjustOverflow={{ adjustX: 1, adjustY: 1 }}
        align={{ overflow: { adjustX: 1, adjustY: 1 } }}
        arrow={false}
        overlayStyle={{
          borderRadius: 12,
          maxWidth: "calc(100vw - 24px)",
          maxHeight: "calc(100vh - 24px)",
        }}
        styles={{
          root: {
            maxWidth: "calc(100vw - 24px)",
          },
          body: {
            borderRadius: 12,
            padding: 12,
            width: FILTER_POPUP_WIDTH,
            maxWidth: "calc(100vw - 24px)",
            overflow: "visible",
            boxShadow: "0 6px 24px rgba(0,0,0,0.12)",
          },
        }}
      >
        <Badge count={activeFilterCount} size="small" offset={[-4, 4]}>
          <Button
            size="large"
            icon={
              <img
                src={filterIcon}
                alt="Filter"
                style={{ width: 16, height: 16 }}
              />
            }
            style={{
              borderRadius: "4px",
              borderColor: activeFilterCount > 0 ? "#1677ff" : undefined,
              color: activeFilterCount > 0 ? "#1677ff" : undefined,
              fontWeight: activeFilterCount > 0 ? 600 : undefined,
              whiteSpace: "nowrap",
            }}
          >
            Filter
          </Button>
        </Badge>
      </Popover>
    </div>
  );
}
